import React, { useState, useEffect, useReducer, useRef } from "react";
import { toast } from "react-toastify";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../../Assets/wa-background-optimized.png";

import api from "../../services/api";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import { i18n } from "../../translate/i18n";
import { operationalActivitySummary } from "./operationalActivity.mjs";

const resolveMediaUrl = mediaUrl => {
  const rawMediaUrl = String(mediaUrl || "").trim();

  if (!rawMediaUrl) {
    return "";
  }

  const backendUrl = getBackendUrl().replace(/\/$/, "");

  if (/^https?:\/\//i.test(rawMediaUrl)) {
    try {
      const parsedUrl = new URL(rawMediaUrl);
      const parsedBackendUrl = new URL(backendUrl);
      const normalizedPath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
      const malformedBackendPath = normalizedPath.match(/^\/backend(?::\d+)?(\/public\/.*)$/i);
      const isInternalHost = /^(localhost|127\.0\.0\.1|0\.0\.0\.0|backend|whaticket-community-backend(?:-\d+)?)$/i.test(parsedUrl.hostname);
      const isInternalPort = parsedUrl.port === "8080";

      if (malformedBackendPath) {
        return `${backendUrl}${malformedBackendPath[1]}`;
      }

      if (parsedUrl.pathname.startsWith("/backend/public/")) {
        return `${parsedBackendUrl.origin}${normalizedPath}`;
      }

      if (
        isInternalHost ||
        isInternalPort ||
        parsedUrl.pathname.startsWith("/public/")
      ) {
        const publicPath = normalizedPath.startsWith("/public/")
          ? normalizedPath
          : `/public/${parsedUrl.pathname.replace(/^\/+/, "")}${parsedUrl.search}${parsedUrl.hash}`;

        return `${backendUrl}${publicPath}`;
      }

      return rawMediaUrl;
    } catch (_urlError) {
      return rawMediaUrl;
    }
  }

  if (rawMediaUrl.startsWith(":/public/")) {
    return `${backendUrl}${rawMediaUrl.slice(1)}`;
  }

  if (rawMediaUrl.startsWith("/public/")) {
    return `${backendUrl}${rawMediaUrl}`;
  }

  if (rawMediaUrl.startsWith("public/")) {
    return `${backendUrl}/${rawMediaUrl}`;
  }

  return `${backendUrl}/public/${rawMediaUrl.replace(/^\/+/, "")}`;
};

const getMediaFilename = mediaUrl => {
  const normalizedUrl = resolveMediaUrl(mediaUrl);

  if (!normalizedUrl) {
    return "";
  }

  const sanitizedUrl = normalizedUrl.split("?")[0];
  return decodeURIComponent(sanitizedUrl.split("/").pop() || "").trim();
};

const getVisibleMessageBody = message => {
  const rawBody = String(message?.body || "").trim();

  if (!rawBody) {
    return "";
  }

  if (!["image", "audio", "video", "document"].includes(message?.mediaType)) {
    return rawBody;
  }

  if (/^\[[a-z_]+\]$/i.test(rawBody)) {
    return "";
  }

  const mediaFilename = getMediaFilename(message?.mediaUrl);
  if (mediaFilename && rawBody === mediaFilename) {
    return "";
  }

  return rawBody;
};

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: 0,
  },

  messagesList: {
    backgroundColor:
      theme.palette.type === "dark" ? theme.palette.background.default : "#efe7dd",
    backgroundImage:
      theme.palette.type === "dark"
        ? `linear-gradient(rgba(18, 18, 18, 0.72), rgba(18, 18, 18, 0.72)), url(${whatsBackground})`
        : `url(${whatsBackground})`,
    backgroundRepeat: "repeat",
    backgroundSize: "380px auto",
    backgroundPosition: "center",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      padding: "12px 8px calc(104px + env(safe-area-inset-bottom)) 8px",
    },
    ...theme.scrollbarStyles,
  },

  previousAttendanceActions: {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(1, 0),
  },

  previousAttendanceButton: {
    borderRadius: 999,
    boxShadow: "none",
    textTransform: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },

  previousAttendanceDivider: {
    alignSelf: "center",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(32, 44, 51, 0.96)" : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    color: theme.palette.text.secondary,
    fontSize: 12,
    margin: theme.spacing(1, 0),
    maxWidth: "88%",
    padding: theme.spacing(0.5, 1.5),
    textAlign: "center",
  },

  previousAttendanceEnd: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: theme.spacing(0.5, 0),
    textAlign: "center",
  },

  internalEventsActions: {
    alignSelf: "center",
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: theme.spacing(0.5, 0),
    textAlign: "center",
  },

  internalEventsEmpty: {
    alignSelf: "center",
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: theme.spacing(0.5, 0),
    textAlign: "center",
  },

  internalEventMessage: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 0,
    maxWidth: "min(600px, calc(100% - 24px))",
    height: "auto",
    display: "block",
    position: "relative",
    whiteSpace: "pre-wrap",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(92, 68, 18, 0.92)"
        : "#fff3c4",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 193, 7, 0.28)"
        : "rgba(160, 112, 0, 0.18)"
    }`,
    color: theme.palette.type === "dark" ? "#fff8e1" : "#3d2b00",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 0 rgba(255, 255, 255, 0.04)"
        : "0 1px 1px #b3b3b3",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 8,
      maxWidth: "calc(100% - 8px)",
    },
  },

  internalEventContactName: {
    display: "flex",
    color: theme.palette.type === "dark" ? "#ffd54f" : "#8a6200",
    fontWeight: 600,
  },

  internalEventTextContent: {
    overflowWrap: "break-word",
    wordBreak: "break-word",
    padding: "3px 80px 6px 6px",
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 0,
    maxWidth: "min(600px, calc(100% - 24px))",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor:
      theme.palette.type === "dark" ? "#202c33" : "#ffffff",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 0 rgba(255, 255, 255, 0.04)"
        : "0 1px 1px #b3b3b3",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    [theme.breakpoints.down("sm")]: {
      marginRight: 8,
      maxWidth: "calc(100% - 8px)",
    },
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor:
      theme.palette.type === "dark" ? "#1d282f" : "#f0f2f5",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 0,
    maxWidth: "min(600px, calc(100% - 24px))",
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor:
      theme.palette.type === "dark" ? "#005c4b" : "#d9fdd3",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 0 rgba(255, 255, 255, 0.04)"
        : "0 1px 1px #b3b3b3",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 8,
      maxWidth: "calc(100% - 8px)",
    },
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor:
      theme.palette.type === "dark" ? "#025144" : "#d1f4cc",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: theme.palette.text.secondary,
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    wordBreak: "break-word",
    padding: "3px 80px 6px 6px",
    "& a": {
      wordBreak: "break-all",
      overflowWrap: "anywhere",
    },
    "& pre, & code": {
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      overflowWrap: "anywhere",
    },
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.46)"
        : "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    padding: "3px 80px 6px 6px",
    "& a": {
      wordBreak: "break-all",
      overflowWrap: "anywhere",
    },
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.type === "dark" ? "#aebac1" : "#667781",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.1)" : "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 1px 0 rgba(255, 255, 255, 0.04)"
        : "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: theme.palette.text.secondary,
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    color: theme.palette.type === "dark" ? "#aebac1" : "#667781",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: theme.palette.type === "dark" ? "#53bdeb" : "#0b93f6",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  unavailableMedia: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontStyle: "italic",
    padding: theme.spacing(1, 1.5, 0.5),
  },
}));

const reducer = (state, action) => {
  const sortMessagesByDate = messages =>
    [...messages].sort(
      (messageA, messageB) =>
        new Date(messageA.createdAt).getTime() - new Date(messageB.createdAt).getTime()
    );

  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return sortMessagesByDate([...newMessages, ...state]);
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return sortMessagesByDate(state);
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return sortMessagesByDate(state);
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup, showInternalEvents = false }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previousAttendances, setPreviousAttendances] = useState([]);
  const [loadingPreviousAttendance, setLoadingPreviousAttendance] = useState(false);
  const [hasPreviousAttendance, setHasPreviousAttendance] = useState(true);
  const [loadingWhatsappHistory, setLoadingWhatsappHistory] = useState(false);
  const [whatsappHistoryLimit, setWhatsappHistoryLimit] = useState(50);
  const [internalEvents, setInternalEvents] = useState([]);
  const [loadingInternalEvents, setLoadingInternalEvents] = useState(false);
  const [internalEventsLoaded, setInternalEventsLoaded] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setPreviousAttendances([]);
    setHasPreviousAttendance(true);
    setLoadingPreviousAttendance(false);
    setLoadingWhatsappHistory(false);
    setWhatsappHistoryLimit(50);
    setInternalEvents([]);
    setLoadingInternalEvents(false);
    setInternalEventsLoaded(false);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const fetchMessages = async () => {
      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber },
        });

        if (currentTicketId.current === ticketId) {
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          setHasMore(data.hasMore);
          setLoading(false);
        }

        if (pageNumber === 1 && data.messages.length > 1) {
          scrollToBottom();
        }
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };

    fetchMessages();
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    const joinCurrentTicketRoom = () => {
      socket.emit("joinChatBox", ticketId);
    };

    socket.on("connect", joinCurrentTicketRoom);

    if (socket.connected) {
      joinCurrentTicketRoom();
    }

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "deliveryError") {
        const errorKey = String(data.error || "ERR_SENDING_WAPP_MSG");
        const toastMessage = i18n.exists(`backendErrors.${errorKey}`)
          ? i18n.t(`backendErrors.${errorKey}`)
          : errorKey;

        toast.error(toastMessage, {
          toastId: `${errorKey}:${data.messageId || "unknown"}`,
        });
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    socket.on("ticketInternalEvent", data => {
      if (Number(data?.ticketId) !== Number(ticketId) || !data?.event) {
        return;
      }

      setInternalEvents(prevEvents => {
        const eventIndex = prevEvents.findIndex(event => event.id === data.event.id);
        if (eventIndex !== -1) {
          prevEvents[eventIndex] = data.event;
          return [...prevEvents];
        }
        return [...prevEvents, data.event];
      });
      setInternalEventsLoaded(true);
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  useEffect(() => {
    let isRefreshing = false;

    const refreshLatestMessages = async () => {
      if (isRefreshing) {
        return;
      }

      isRefreshing = true;

      try {
        const { data } = await api.get("/messages/" + ticketId, {
          params: { pageNumber: 1 },
        });

        if (currentTicketId.current === ticketId) {
          dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          setHasMore(data.hasMore);
        }
      } catch (_err) {
        // Keep silent fallback refresh to avoid noisy toasts.
      } finally {
        isRefreshing = false;
      }
    };

    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshLatestMessages();
      }
    }, 15000);

    const handleFocus = () => {
      refreshLatestMessages();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshLatestMessages();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const reloadLatestMessages = async () => {
    const { data } = await api.get("/messages/" + ticketId, {
      params: { pageNumber: 1 },
    });

    if (currentTicketId.current !== ticketId) {
      return;
    }

    dispatch({ type: "RESET" });
    dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
    setHasMore(data.hasMore);
    setPageNumber(1);
  };

  const handleLoadPreviousAttendance = async () => {
    if (loadingPreviousAttendance || !hasPreviousAttendance) {
      return;
    }

    setLoadingPreviousAttendance(true);

    try {
      const beforeTicketId =
        previousAttendances.length > 0
          ? previousAttendances[0].ticket.id
          : ticketId;
      const { data } = await api.get(
        `/messages/${ticketId}/previous-attendance`,
        {
          params: { beforeTicketId },
        }
      );

      if (currentTicketId.current !== ticketId) {
        return;
      }

      if (!data?.ticket) {
        setHasPreviousAttendance(false);
        return;
      }

      setPreviousAttendances(prevAttendances => [
        {
          ticket: data.ticket,
          messages: Array.isArray(data.messages) ? data.messages : [],
        },
        ...prevAttendances,
      ]);
    } catch (err) {
      toastError(err);
    } finally {
      if (currentTicketId.current === ticketId) {
        setLoadingPreviousAttendance(false);
      }
    }
  };

  const handleSyncWhatsappHistory = async () => {
    if (loadingWhatsappHistory) {
      return;
    }

    setLoadingWhatsappHistory(true);

    try {
      const { data } = await api.post(
        `/messages/${ticketId}/sync-whatsapp-history`,
        { limit: whatsappHistoryLimit }
      );

      if (currentTicketId.current !== ticketId) {
        return;
      }

      if (Array.isArray(data?.messages) && data.messages.length > 0) {
        dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
      }

      await reloadLatestMessages();

      if (data?.nextLimit) {
        setWhatsappHistoryLimit(data.nextLimit);
      }

      if (Number(data?.importedCount || 0) > 0) {
        toast.success(
          i18n.t("messagesList.previousAttendance.whatsappHistoryImported", {
            count: data.importedCount,
          })
        );
      } else {
        toast.info(i18n.t("messagesList.previousAttendance.whatsappHistoryEmpty"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (currentTicketId.current === ticketId) {
        setLoadingWhatsappHistory(false);
      }
    }
  };

  useEffect(() => {
    if (!showInternalEvents || internalEventsLoaded || loadingInternalEvents) {
      return;
    }

    let mounted = true;
    let fallbackTimeoutId = null;
    setLoadingInternalEvents(true);

    const fetchInternalEvents = async () => {
      fallbackTimeoutId = setTimeout(() => {
        if (mounted && currentTicketId.current === ticketId) {
          setInternalEventsLoaded(true);
          setLoadingInternalEvents(false);
        }
      }, 15000);

      try {
        const { data } = await api.get(`/messages/${ticketId}/internal-events`, {
          timeout: 15000,
        });

        if (mounted && currentTicketId.current === ticketId) {
          setInternalEvents(Array.isArray(data.events) ? data.events : []);
          setInternalEventsLoaded(true);
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (fallbackTimeoutId) {
          clearTimeout(fallbackTimeoutId);
        }
        if (mounted && currentTicketId.current === ticketId) {
          setInternalEventsLoaded(true);
          setLoadingInternalEvents(false);
        }
      }
    };

    fetchInternalEvents();

    return () => {
      mounted = false;
      if (fallbackTimeoutId) {
        clearTimeout(fallbackTimeoutId);
      }
    };
  }, [showInternalEvents, internalEventsLoaded, loadingInternalEvents, ticketId]);

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    const normalizedMediaUrl = resolveMediaUrl(message.mediaUrl);
    const mediaNeedsFile = ["image", "audio", "video", "document", "sticker"].includes(message.mediaType);

    if (mediaNeedsFile && !normalizedMediaUrl) {
      return (
        <div className={classes.unavailableMedia}>
          {i18n.t(`messagesList.mediaUnavailable.${message.mediaType}`, {
            defaultValue: i18n.t("messagesList.mediaUnavailable.default"),
          })}
        </div>
      );
    }

    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if ( /^.*\.(jpe?g|png|gif|webp)(\?.*)?$/i.exec(normalizedMediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={normalizedMediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={normalizedMediaUrl} />
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={normalizedMediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={normalizedMediaUrl}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (
    message,
    index,
    sourceMessages = messagesList,
    options = {}
  ) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`${options.keyPrefix || ""}timestamp-${message.kind || "message"}-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(sourceMessages[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < sourceMessages.length - 1) {
      let messageDay = parseISO(sourceMessages[index].createdAt);
      let previousMessageDay = parseISO(sourceMessages[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
          className={classes.dailyTimestamp}
          key={`${options.keyPrefix || ""}timestamp-${message.kind || "message"}-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(sourceMessages[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (options.attachLastRef && index === sourceMessages.length - 1) {
      return (
        <div
          key={`${options.keyPrefix || ""}ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessageDivider = (
    message,
    index,
    sourceMessages = messagesList,
    keyPrefix = ""
  ) => {
    if (index < sourceMessages.length && index > 0) {
      if (
        sourceMessages[index]?.kind !== "message" ||
        sourceMessages[index - 1]?.kind !== "message"
      ) {
        return null;
      }

      let messageUser = sourceMessages[index].fromMe;
      let previousMessageUser = sourceMessages[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`${keyPrefix}divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {message.quotedMsg?.body}
        </div>
      </div>
    );
  };

  const buildTimelineItems = sourceMessages => {
    const messageItems = sourceMessages.map(message => ({
      ...message,
      kind: "message",
      timelineId: `message-${message.id}`,
    }));

    if (!showInternalEvents) {
      return messageItems;
    }

    return [
      ...messageItems,
      ...internalEvents.map(event => ({
        ...event,
        kind: "internalEvent",
        timelineId: `internal-event-${event.id}`,
      })),
    ].sort((a, b) => {
      const dateDiff =
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

      if (dateDiff !== 0) {
        return dateDiff;
      }

      if (a.kind === b.kind) {
        return Number(a.id || 0) - Number(b.id || 0);
      }

      return a.kind === "message" ? -1 : 1;
    });
  };

  const getInternalEventAuthor = event =>
    event.actorName || i18n.t("messagesList.internalEvents.systemAuthor");

  const getInternalEventTitle = event => {
    const typeKey = `messagesList.internalEvents.types.${event.type}`;
    return i18n.exists(typeKey) ? i18n.t(typeKey) : event.type;
  };

  const renderInternalEventMessage = (
    event,
    index,
    sourceMessages,
    keyPrefix,
    attachLastRef = false
  ) => (
    <React.Fragment key={`${keyPrefix}${event.timelineId || `internal-event-${event.id}`}`}>
      {renderDailyTimestamps(event, index, sourceMessages, {
        keyPrefix,
      })}
      <div className={classes.internalEventMessage}>
        <div className={classes.internalEventTextContent}>
          <span className={classes.internalEventContactName}>
            {getInternalEventAuthor(event)}
          </span>
          <MarkdownWrapper>{operationalActivitySummary(event, key => i18n.t(key), key => i18n.exists(key))}</MarkdownWrapper>
          <span className={classes.timestamp}>
            {format(parseISO(event.createdAt), "HH:mm")}
          </span>
        </div>
      </div>
      {attachLastRef && index === sourceMessages.length - 1 && (
        <div
          key={`${keyPrefix}ref-${event.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      )}
    </React.Fragment>
  );

  const renderMessagesFrom = (
    sourceMessages,
    { historical = false, keyPrefix = "", attachLastRef = false } = {}
  ) => {
    const timelineMessages = historical
      ? sourceMessages.map(message => ({
          ...message,
          kind: "message",
          timelineId: `message-${message.id}`,
        }))
      : buildTimelineItems(sourceMessages);

    if (timelineMessages.length > 0) {
      const viewMessagesList = timelineMessages.map((message, index) => {
        if (message.kind === "internalEvent") {
          return renderInternalEventMessage(
            message,
            index,
            timelineMessages,
            keyPrefix,
            attachLastRef
          );
        }

        const visibleBody = getVisibleMessageBody(message);

        if (!message.fromMe) {
          return (
            <React.Fragment key={`${keyPrefix}${message.timelineId || message.id}`}>
              {renderDailyTimestamps(message, index, timelineMessages, {
                attachLastRef,
                keyPrefix,
              })}
              {renderMessageDivider(message, index, timelineMessages, keyPrefix)}
              <div className={classes.messageLeft}>
                {!historical && (
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                )}
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl || ["image", "audio", "video", "document", "sticker", "location", "vcard"].includes(message.mediaType)
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {visibleBody && <MarkdownWrapper>{visibleBody}</MarkdownWrapper>}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={`${keyPrefix}${message.timelineId || message.id}`}>
              {renderDailyTimestamps(message, index, timelineMessages, {
                attachLastRef,
                keyPrefix,
              })}
              {renderMessageDivider(message, index, timelineMessages, keyPrefix)}
              <div className={classes.messageRight}>
                {!historical && (
                  <IconButton
                    variant="contained"
                    size="small"
                    id="messageActionsButton"
                    disabled={message.isDeleted}
                    className={classes.messageActionsButton}
                    onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                  >
                    <ExpandMore />
                  </IconButton>
                )}
                {(message.mediaUrl || ["image", "audio", "video", "document", "sticker", "location", "vcard"].includes(message.mediaType)
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {visibleBody && <MarkdownWrapper>{visibleBody}</MarkdownWrapper>}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return [];
    }
  };

  const renderPreviousAttendances = () =>
    previousAttendances.map(previousAttendance => {
      const createdAtLabel = previousAttendance.ticket?.createdAt
        ? format(parseISO(previousAttendance.ticket.createdAt), "dd/MM/yyyy HH:mm")
        : "";
      const queueName = previousAttendance.ticket?.queue?.name
        ? ` - ${previousAttendance.ticket.queue.name}`
        : "";

      return (
        <React.Fragment key={`previous-attendance-${previousAttendance.ticket.id}`}>
          <div className={classes.previousAttendanceDivider}>
            {`${i18n.t("messagesList.previousAttendance.title")} #${previousAttendance.ticket.id}`}
            {createdAtLabel ? ` - ${createdAtLabel}` : ""}
            {queueName}
          </div>
          {renderMessagesFrom(previousAttendance.messages, {
            historical: true,
            keyPrefix: `previous-${previousAttendance.ticket.id}-`,
          })}
        </React.Fragment>
      );
    });

  const renderInternalEventsStatus = () => {
    if (!showInternalEvents) {
      return null;
    }

    if (internalEvents.length > 0) {
      return null;
    }

    if (!loadingInternalEvents && !internalEventsLoaded) {
      return null;
    }

    return (
      <span className={classes.internalEventsEmpty}>
        {loadingInternalEvents
          ? i18n.t("messagesList.internalEvents.loading")
          : i18n.t("messagesList.internalEvents.empty")}
      </span>
    );
  };

  const renderPreviousAttendanceAction = () => {
    if (hasMore || messagesList.length === 0) {
      return null;
    }

    return (
      <div className={classes.previousAttendanceActions}>
        {hasPreviousAttendance ? (
          <Button
            className={classes.previousAttendanceButton}
            color="primary"
            disabled={loadingPreviousAttendance}
            disableElevation
            onClick={handleLoadPreviousAttendance}
            size="small"
            variant="contained"
          >
            {loadingPreviousAttendance
              ? i18n.t("messagesList.previousAttendance.loading")
              : i18n.t("messagesList.previousAttendance.load")}
          </Button>
        ) : (
          <>
            <span className={classes.previousAttendanceEnd}>
              {i18n.t("messagesList.previousAttendance.none")}
            </span>
            <Button
              className={classes.previousAttendanceButton}
              color="primary"
              disabled={loadingWhatsappHistory}
              disableElevation
              onClick={handleSyncWhatsappHistory}
              size="small"
              variant="outlined"
            >
              {loadingWhatsappHistory
                ? i18n.t("messagesList.previousAttendance.whatsappHistoryLoading")
                : i18n.t("messagesList.previousAttendance.whatsappHistoryLoad")}
            </Button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {renderPreviousAttendances()}
        {renderPreviousAttendanceAction()}
        {renderInternalEventsStatus()}
        {messagesList.length > 0
          ? renderMessagesFrom(messagesList, { attachLastRef: true })
          : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
